import { DragStartEvent, DragEndEvent, useDndMonitor, DragOverEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import debounce from 'lodash/debounce';
import { insert } from 'ramda';
import React, { ReactNode, useState, useMemo, useCallback } from 'react';

import { changeSubcategoryParentCategory, updateCategoryOrder, updateSubcategoryOrder } from 'modules/shared-menu/api';
import { SelectionCategory, SharedMenu } from 'modules/shared-menu/types';

import { useSelectionData } from '../../../../../hooks/useSelectionData';
import { MenuEditorDragEventData } from '../types';
import { getMenuEditorDragEventData } from '../utils';

type Props = {
  categories: SelectionCategory[];
  children: (data: { displayCategories: SelectionCategory[]; isSortingCategory: boolean }) => ReactNode;
};

const CategoryNavigationDndMonitor = React.memo(({ categories, children }: Props) => {
  // Use cached active due to the issue with virtualized lists cause the active.data.current to get lost: https://github.com/clauderic/dnd-kit/issues/794
  const [activeEvent, setActiveEvent] = useState<null | MenuEditorDragEventData>(null);
  const [localCategories, setLocalCategories] = useState<null | SelectionCategory[]>(null);
  const displayCategories = useMemo(() => localCategories ?? categories ?? [], [categories, localCategories]);

  const { selection, updateSelectionData } = useSelectionData();

  const reset = useCallback(() => {
    setLocalCategories(null);
    setActiveEvent(null);
  }, []);

  const onDragStart = useCallback(({ active }: DragStartEvent) => {
    setActiveEvent(active ? getMenuEditorDragEventData(active) : null);
  }, []);

  const onDragEnd = useCallback(
    async ({ over }: DragEndEvent) => {
      const overEvent = over && getMenuEditorDragEventData(over);
      try {
        if (!overEvent || !activeEvent) {
          return;
        }

        // Change position of a normal category or a parent category (of subcategories)
        if (
          activeEvent.id !== overEvent.id &&
          ['DND_NAVIGATION_CATEGORY', 'DND_ACCORDION_NAVIGATION_CATEGORY'].includes(activeEvent.type) &&
          ['DND_NAVIGATION_CATEGORY', 'DND_ACCORDION_NAVIGATION_CATEGORY'].includes(overEvent.type)
        ) {
          const oldIndex = categories.findIndex(category => category.id === activeEvent.id);
          const newIndex = categories.findIndex(category => category.id === over.id);
          const updatedCategories = arrayMove(categories, oldIndex, newIndex);

          setLocalCategories(updatedCategories);

          await updateCategoryOrder(
            selection.id,
            updatedCategories.map(category => category.id),
          );
          const updatedSelection: SharedMenu = {
            ...selection,
            categories: updatedCategories,
          };
          updateSelectionData({ actionType: 'updateSelection', payload: updatedSelection });
        }

        // Change order of subcategories inside the same parent category
        if (
          activeEvent.id !== overEvent.id &&
          activeEvent.type === 'DND_NAVIGATION_SUBCATEGORY' &&
          overEvent.type === 'DND_NAVIGATION_SUBCATEGORY' &&
          overEvent.parentCategoryId === activeEvent.parentCategoryId
        ) {
          const categorySubcategories = categories.find(
            category => category.id === activeEvent.parentCategoryId,
          )?.subcategories;
          if (!categorySubcategories) {
            return;
          }

          const oldIndex = categorySubcategories.findIndex(category => category.id === activeEvent.id);
          const newIndex = categorySubcategories.findIndex(category => category.id === over.id);
          const updatedSubcategories = arrayMove(categorySubcategories, oldIndex, newIndex);
          const updatedCategories = categories.map(category => ({
            ...category,
            subcategories: category.id === activeEvent.parentCategoryId ? updatedSubcategories : category.subcategories,
          }));

          setLocalCategories(updatedCategories);

          const updatedSelection = await updateSubcategoryOrder(
            selection.id,
            activeEvent.parentCategoryId,
            updatedSubcategories.map(subcategory => subcategory.id),
          );
          updateSelectionData({ actionType: 'updateSelection', payload: updatedSelection });
        }

        // Change parent category of a subcategory
        if (
          activeEvent.type === 'DND_NAVIGATION_SUBCATEGORY' &&
          ['DND_NAVIGATION_SUBCATEGORY', 'DND_NAVIGATION_CATEGORY', 'DND_ACCORDION_NAVIGATION_CATEGORY'].includes(
            overEvent.type,
          )
        ) {
          const overParentCategoryId =
            overEvent.type === 'DND_NAVIGATION_SUBCATEGORY'
              ? overEvent.parentCategoryId
              : overEvent.type === 'DND_NAVIGATION_CATEGORY'
                ? overEvent.id
                : overEvent.type === 'DND_ACCORDION_NAVIGATION_CATEGORY'
                  ? overEvent.category.id
                  : '';
          const overSubcategories = displayCategories.find(
            category => category.id === overParentCategoryId,
          )?.subcategories;

          if (!overSubcategories || activeEvent.parentCategoryId === overParentCategoryId) {
            return;
          }

          const oldIndex = overSubcategories.findIndex(category => category.id === activeEvent.id);
          const newIndex = ['DND_ACCORDION_NAVIGATION_CATEGORY', 'DND_NAVIGATION_CATEGORY'].includes(overEvent.type)
            ? 0
            : overSubcategories.findIndex(category => category.id === overEvent.id);
          if (oldIndex === -1 || newIndex === -1) {
            return;
          }
          const updatedSubcategories = arrayMove(overSubcategories, oldIndex, newIndex);
          const updatedCategories = displayCategories.map(category => ({
            ...category,
            subcategories:
              category.id === activeEvent.parentCategoryId
                ? category.subcategories?.filter(subcategory => subcategory.id !== activeEvent.id)
                : category.id === overParentCategoryId
                  ? updatedSubcategories
                  : category.subcategories,
          }));

          setLocalCategories(updatedCategories);

          await changeSubcategoryParentCategory(selection.id, activeEvent.id, overParentCategoryId);
          const updatedSelection = await updateSubcategoryOrder(
            selection.id,
            overParentCategoryId,
            updatedSubcategories.map(subcategory => subcategory.id),
          );
          updateSelectionData({ actionType: 'updateSelection', payload: updatedSelection });
        }
      } finally {
        reset();
      }
      return;
    },
    [activeEvent, categories, displayCategories, reset, selection, updateSelectionData],
  );

  const onDragOver = useCallback(
    ({ over, active }: DragOverEvent) => {
      const overEvent = over && getMenuEditorDragEventData(over);
      const activeEvent = active && getMenuEditorDragEventData(active);
      if (!overEvent || !activeEvent) {
        return;
      }

      // Hovering a subcategory over a ("normal") category
      if (
        overEvent.type === 'DND_NAVIGATION_CATEGORY' &&
        activeEvent.type === 'DND_NAVIGATION_SUBCATEGORY' &&
        !overEvent.category.offer_bindings?.length &&
        overEvent.category.id !== activeEvent.parentCategoryId
      ) {
        const categoriesState = localCategories || categories;
        const updatedState = categoriesState.map(category => {
          if (category.id === activeEvent.parentCategoryId) {
            return {
              ...category,
              subcategories: (category.subcategories ?? []).filter(subcategory => subcategory.id !== activeEvent.id),
            };
          }

          if (category.id === overEvent.category.id) {
            return {
              ...category,
              subcategories: [activeEvent.subcategory],
            };
          }

          return category;
        });

        setLocalCategories(updatedState);
      }

      // Hovering a subcategory over another subcategory/parent category
      if (
        ['DND_NAVIGATION_SUBCATEGORY', 'DND_ACCORDION_NAVIGATION_CATEGORY'].includes(overEvent.type) &&
        activeEvent.type === 'DND_NAVIGATION_SUBCATEGORY'
      ) {
        if (
          (overEvent.type === 'DND_NAVIGATION_SUBCATEGORY' &&
            overEvent.parentCategoryId === activeEvent.parentCategoryId) ||
          (overEvent.type === 'DND_ACCORDION_NAVIGATION_CATEGORY' &&
            overEvent.category.id === activeEvent.parentCategoryId)
        ) {
          return;
        }

        const categoriesState = localCategories || categories;
        const updatedState = categoriesState.map(category => {
          if (category.id === activeEvent.parentCategoryId) {
            return {
              ...category,
              subcategories: (category.subcategories ?? []).filter(subcategory => subcategory.id !== activeEvent.id),
            };
          }

          if (overEvent.type === 'DND_NAVIGATION_SUBCATEGORY' && category.id === overEvent.parentCategoryId) {
            const subcategoryIndex = (category.subcategories ?? []).findIndex(
              subcategory => subcategory.id === overEvent.id,
            );
            return {
              ...category,
              subcategories: insert(subcategoryIndex, activeEvent.subcategory, category.subcategories ?? []),
            };
          }

          if (overEvent.type === 'DND_ACCORDION_NAVIGATION_CATEGORY' && category.id === overEvent.category.id) {
            return {
              ...category,
              subcategories: insert(0, activeEvent.subcategory, category.subcategories ?? []),
            };
          }

          return category;
        });

        setLocalCategories(updatedState);
      }
    },
    [categories, localCategories],
  );

  useDndMonitor({
    onDragStart,
    onDragEnd,
    onDragCancel: reset,
    onDragOver: debounce(onDragOver, 100),
  });

  return (
    <>
      {children({
        displayCategories,
        isSortingCategory: !!(activeEvent?.type === 'DND_NAVIGATION_CATEGORY'),
      })}
    </>
  );
});

export default CategoryNavigationDndMonitor;
